import { i18n, useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import NextI18nextConfig from "../../next-i18next.config";
import { animalPronoun, speciesName } from "../utils/insurance";
import { getOrg } from "../utils/site";
import { NullableString } from "types";
import { isDefined } from "utils/typescript";
import { Locale } from "config/site";

export { Trans } from "./components";

export const genitiveCase = (name: NullableString, locale?: string): string => {
  if (!locale || !isDefined(name) || name === "") {
    return `${name}`;
  }

  switch (locale as Locale) {
    case "en": {
      return `${name}'s`;
    }

    case "sv": {
      const lastLetter = name?.toLowerCase().substring(name.length - 1);

      return ["s", "x", "z"].includes(lastLetter) ? name : `${name}s`;
    }

    case "de": {
      const lastLetter = name?.toLowerCase().substring(name.length - 1) || "";

      return ["s", "x", "z", "ß"].includes(lastLetter)
        ? `${name}'`
        : `${name}s`;
    }

    case "fr": {
      const firstLetter = name?.toLocaleLowerCase().substring(0, 1) || "";

      return ["a", "e", "i", "o", "u"].includes(firstLetter)
        ? `d'${name}`
        : `de ${name}`;
    }

    default:
      return name;
  }
};

export const joinArray = (arr: [string]): string => {
  const andLower = i18n?.t("format.lowercase", {
    str: i18n?.t("global.and"),
  });
  return arr.join(", ").replace(/,([^,]*)$/, ` ${andLower}$1`);
};

export const useWebTranslation = (): {
  t: (
    key: string,
    options?: Record<string, string | number | boolean>
  ) => string;
  i18n: typeof i18n;
  ready: boolean;
} => {
  const { t, i18n: i18nT, ready } = useTranslation("web");

  const formatter = i18nT?.services?.formatter;
  formatter?.add("uppercase", (value) => {
    return value.toUpperCase();
  });
  formatter?.add("lowercase", (value) => {
    return value.toLowerCase();
  });
  formatter?.add("genitive_case", (value, lng) => {
    return genitiveCase(value, lng);
  });
  formatter?.add("species_name", (value) => {
    return speciesName(value);
  });
  formatter?.add("join_array", (value) => {
    return joinArray(value);
  });
  formatter?.add("animal_pronoun", (value, _lng, options) => {
    return animalPronoun(options.sex, value);
  });

  return {
    t: (key, options) => t(key, { ...options, context: getOrg() }),
    i18n: i18nT,
    ready,
  };
};

export const useTranslationsReady = () => {
  const [ready, setReady] = useState(false);
  const { ready: webReady } = useWebTranslation();
  useEffect(() => {
    setReady(webReady);
  }, [webReady]);
  return ready;
};

export const namespaces = NextI18nextConfig.ns;

export const getWebTranslation = (
  key: string,
  values?: Record<string, unknown>
) => {
  return i18n?.t(key, { ...values, ns: "web", context: getOrg() }) || "";
};

export { LinkT, EmailT, transHOC } from "./components";

export { i18n };
